import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Home, Tag } from "lucide-react";
import { toast } from "sonner";
import { BUYER_QUESTIONS, SELLER_QUESTIONS, AREA_GROUPS } from "../data";
import { Reveal } from "./Reveal";
import { SellerGuide } from "./SellerGuide";
import { trackLead } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESULTS = {
  buyer: {
    title: "Your dream home is closer than you think",
    body: "Based on your answers, Melissa will build a tailored buying strategy — surfacing the right listings (including off-market and foreclosure opportunities) and guiding you from offer to keys."
  },
  seller: {
    title: "Let's get you top dollar",
    body: "Based on your answers, Melissa will prepare a data-driven pricing strategy and a modern marketing plan to sell your property faster and for more."
  }
};

const InputBase = "w-full bg-white border border-black/20 px-4 py-3 text-base focus:outline-none focus:ring-1 focus:ring-[#0a0a0a] focus:border-[#0a0a0a] transition-colors";

export const Quiz = () => {
  const [role, setRole] = useState(null); // "buyer" | "seller"
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({
    name: "", email: "", phone: "", preferred_contact: "email", preferred_time: ""
  });

  const questions = role === "buyer" ? BUYER_QUESTIONS : SELLER_QUESTIONS;
  const totalSteps = questions.length + 1; // questions + contact step
  const progress = role ? Math.round(((step) / totalSteps) * 100) : 0;

  const reset = () => {
    setRole(null); setStep(0); setAnswers({}); setSubmitted(false);
    setLead({ name: "", email: "", phone: "", preferred_contact: "email", preferred_time: "" });
  };

  const pick = (qid, option) => {
    setAnswers((a) => ({ ...a, [qid]: option }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const submit = async () => {
    if (!lead.name.trim()) return toast.error("Please enter your name");
    if (lead.preferred_contact === "email" && !lead.email.trim()) return toast.error("Please enter your email");
    if (lead.preferred_contact === "phone" && !lead.phone.trim()) return toast.error("Please enter your phone number");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        source: `${role}_quiz`,
        name: lead.name,
        email: lead.email || null,
        phone: lead.phone || null,
        preferred_contact: lead.preferred_contact,
        preferred_time: lead.preferred_time || null,
        quiz_answers: answers,
        quiz_summary: `${role} quiz`
      });
      trackLead({ source: `${role}_quiz`, area: answers.area });
      setSubmitted(true);
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quiz" data-testid="quiz-section" className="py-24 md:py-32 bg-surface">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center">
            <p className="eyebrow text-gold mb-4">Find Your Agent</p>
            <h2 className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-none">
              What are you looking for?
            </h2>
            <p className="text-[#52525b] font-light text-lg mt-6">
              Take our quick quiz and we'll connect you with Melissa, your way.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 bg-white border border-black/5 p-8 sm:p-12 min-h-[420px] flex flex-col">
          {/* Role selection */}
          {!role && (
            <div className="grid sm:grid-cols-2 gap-6 my-auto">
              <button
                data-testid="quiz-start-buyer"
                onClick={() => setRole("buyer")}
                className="group border border-black/10 hover:border-gold hover:bg-gold/5 transition-all p-10 text-left"
              >
                <Home className="text-gold" size={32} strokeWidth={1.5} />
                <h3 className="font-heading text-2xl mt-6">I'm Buying</h3>
                <p className="text-[#52525b] font-light text-sm mt-2">Find the right realtor to help you buy.</p>
                <span className="inline-flex items-center gap-2 text-sm mt-6 group-hover:text-gold transition-colors">
                  Start buyer quiz <ArrowRight size={16} />
                </span>
              </button>
              <button
                data-testid="quiz-start-seller"
                onClick={() => setRole("seller")}
                className="group border border-black/10 hover:border-gold hover:bg-gold/5 transition-all p-10 text-left"
              >
                <Tag className="text-gold" size={32} strokeWidth={1.5} />
                <h3 className="font-heading text-2xl mt-6">I'm Selling</h3>
                <p className="text-[#52525b] font-light text-sm mt-2">Find the right realtor to sell your home.</p>
                <span className="inline-flex items-center gap-2 text-sm mt-6 group-hover:text-gold transition-colors">
                  Start seller quiz <ArrowRight size={16} />
                </span>
              </button>
            </div>
          )}

          {/* Quiz flow */}
          {role && !submitted && (
            <>
              <div className="flex items-center justify-between mb-8">
                <span className="eyebrow text-[11px] text-[#a1a1aa]" data-testid="quiz-role-label">
                  {role} · Step {Math.min(step + 1, totalSteps)} of {totalSteps}
                </span>
                <div className="w-40 h-px bg-black/10 relative">
                  <div className="absolute left-0 top-0 h-px bg-gold transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <AnimatePresence mode="wait">
                {step < questions.length ? (
                  <motion.div
                    key={`q-${step}`}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.35 }}
                    className="flex-1"
                  >
                    <h3 className="font-heading text-2xl sm:text-3xl leading-snug mb-8" data-testid="quiz-question">
                      {questions[step].q}
                    </h3>
                    {questions[step].id === "area" ? (
                      <div className="space-y-3">
                        <select
                          data-testid="quiz-area-select"
                          className={InputBase}
                          value={answers.area || ""}
                          onChange={(e) => e.target.value && pick("area", e.target.value)}
                        >
                          <option value="">Select your area or a nearby city...</option>
                          {AREA_GROUPS.map((g) => (
                            <optgroup key={g.label} label={g.label}>
                              {g.options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </optgroup>
                          ))}
                        </select>
                        {(() => {
                          const extra = questions[step].options[questions[step].options.length - 1];
                          const active = answers.area === extra;
                          return (
                            <button
                              data-testid={`quiz-option-${extra.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => pick("area", extra)}
                              className={`w-full flex items-center justify-between text-left p-5 border transition-all ${
                                active ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 hover:border-gold hover:bg-gold/5"
                              }`}
                            >
                              <span className="font-light">{extra}</span>
                              {active && <Check size={18} />}
                            </button>
                          );
                        })()}
                      </div>
                    ) : (
                    <div className="space-y-3">
                      {questions[step].options.map((opt) => {
                        const active = answers[questions[step].id] === opt;
                        return (
                          <button
                            key={opt}
                            data-testid={`quiz-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => pick(questions[step].id, opt)}
                            className={`w-full flex items-center justify-between text-left p-5 border transition-all ${
                              active ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 hover:border-gold hover:bg-gold/5"
                            }`}
                          >
                            <span className="font-light">{opt}</span>
                            {active && <Check size={18} />}
                          </button>
                        );
                      })}
                    </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="contact"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.35 }}
                    className="flex-1"
                  >
                    <h3 className="font-heading text-2xl sm:text-3xl leading-snug mb-2">Almost there.</h3>
                    <p className="text-[#52525b] font-light mb-7">How would you like Melissa to reach you?</p>

                    <div className="space-y-4">
                      <input
                        data-testid="quiz-name"
                        className={InputBase}
                        placeholder="Your name"
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                      />

                      <div className="grid grid-cols-2 gap-3">
                        {["email", "phone"].map((m) => (
                          <button
                            key={m}
                            data-testid={`quiz-contact-${m}`}
                            onClick={() => setLead({ ...lead, preferred_contact: m })}
                            className={`p-4 border text-sm capitalize transition-all ${
                              lead.preferred_contact === m ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 hover:border-gold"
                            }`}
                          >
                            Contact me by {m}
                          </button>
                        ))}
                      </div>

                      {lead.preferred_contact === "email" ? (
                        <input
                          data-testid="quiz-email"
                          type="email"
                          className={InputBase}
                          placeholder="Email address"
                          value={lead.email}
                          onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        />
                      ) : (
                        <input
                          data-testid="quiz-phone"
                          type="tel"
                          className={InputBase}
                          placeholder="Phone number"
                          value={lead.phone}
                          onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                        />
                      )}

                      <input
                        data-testid="quiz-time"
                        className={InputBase}
                        placeholder="Best time to reach you (e.g. Weekday mornings)"
                        value={lead.preferred_time}
                        onChange={(e) => setLead({ ...lead, preferred_time: e.target.value })}
                      />

                      <button
                        data-testid="quiz-submit"
                        onClick={submit}
                        disabled={loading}
                        className="w-full bg-gold text-white py-4 text-sm tracking-wide hover:bg-gold-hover transition-colors disabled:opacity-60"
                      >
                        {loading ? "Submitting..." : "See My Results"}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-between items-center mt-8 pt-6 border-t border-black/5">
                <button
                  data-testid="quiz-back"
                  onClick={() => (step === 0 ? reset() : setStep((s) => s - 1))}
                  className="inline-flex items-center gap-2 text-sm text-[#52525b] hover:text-[#0a0a0a] transition-colors"
                >
                  <ArrowLeft size={16} /> {step === 0 ? "Change path" : "Back"}
                </button>
                <button onClick={reset} data-testid="quiz-restart" className="text-sm text-[#a1a1aa] hover:text-[#0a0a0a] transition-colors">
                  Start over
                </button>
              </div>
            </>
          )}

          {/* Result */}
          {submitted && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="my-auto text-center"
              data-testid="quiz-result"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 mb-6">
                <Check className="text-gold" size={30} />
              </div>
              <h3 className="font-heading text-3xl sm:text-4xl font-light">{RESULTS[role].title}</h3>
              <p className="text-[#52525b] font-light text-lg mt-5 max-w-xl mx-auto leading-relaxed">
                {RESULTS[role].body}
              </p>
              <div className="bg-surface border border-black/5 p-6 mt-8 text-left max-w-md mx-auto">
                <p className="eyebrow text-[11px] text-[#a1a1aa] mb-3">What happens next</p>
                <p className="font-light text-sm text-[#52525b]">
                  Thanks, <span className="text-[#0a0a0a] font-medium">{lead.name}</span>. Melissa will reach out
                  by <span className="text-[#0a0a0a] font-medium">{lead.preferred_contact}</span>
                  {lead.preferred_time ? <> around <span className="text-[#0a0a0a] font-medium">{lead.preferred_time}</span></> : null} to
                  craft your personalized {role === "buyer" ? "buying" : "selling"} plan.
                </p>
              </div>
              {role === "seller" && (
                <div className="max-w-md mx-auto">
                  <SellerGuide compact />
                </div>
              )}
              <button
                data-testid="quiz-result-restart"
                onClick={reset}
                className="mt-8 inline-flex items-center gap-2 text-sm border border-black/20 px-7 py-3 hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                Take the quiz again
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
